/**
 * Users API Service
 * Handles all user, person, and role management API calls
 */

import { get, post, put, del, upload } from './client';

// =============================================================================
// User Endpoints
// =============================================================================

/**
 * Get current authenticated user
 * @returns {Promise} Current user data
 */
export const getCurrentUser = () => get('/users/me');

/**
 * Every role assignment the signed-in user holds, with the scope each was
 * granted at. A person legitimately holds several, which the flat `user.role`
 * on the login response cannot express.
 */
export const getMyAccess = () => get('/users/me/access');

/** The same, for any user, looked up by email. 404 if the address is unknown. */
export const checkUserAccess = (email) => get('/users/access-check', { email });

/**
 * Does an account already exist for this email?
 *
 * Drives the Add user panel: an existing account is ADOPTED — its password and
 * status are untouched and it simply gains the roles — so the panel hides the
 * name and password fields and says which of the two is about to happen.
 */
export const lookupUserByEmail = (email) => get('/users/lookup', { email });

/**
 * Create or adopt an account and grant it roles across one or more scopes.
 *
 * Writes the VALID (role x scope) pairs, not the full cross product: a role
 * belongs to one organization, so pairing it with another's centre is
 * meaningless. Invalid pairs come back in `skipped_pairs`.
 *
 * `password_mode` defaults to `self_service` — no password is set, the account
 * is created `pending`, and the existing invite flow emails a link for the
 * person to set their own.
 */
export const grantAccess = (data) => post('/users/grant-access', data);

/**
 * Update current user profile
 * @param {Object} data - Profile data to update
 * @returns {Promise} Updated user data
 */
export const updateCurrentUser = (data) => put('/users/me', data);

/**
 * List all users with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.status - Filter by status (active, inactive, pending)
 * @param {string} params.search - Search term for name/email
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of users
 */
export const listUsers = (params = {}) => get('/users', params);

/**
 * Get user by ID
 * @param {string} userId - User UUID
 * @returns {Promise} User data
 */
export const getUser = (userId) => get(`/users/${userId}`);

/**
 * MFA summary for one user (admin detail drawer): whether MFA is on, the
 * effective default method, and the methods their organization allows.
 * @param {string} userId - User UUID
 * @returns {Promise} { mfa_enabled, default_method, methods: [code] }
 */
export const getUserMfaSummary = (userId) => get(`/users/${userId}/mfa-summary`);

/**
 * Create new user (admin action).
 *
 * Takes no password: the account is created in 'pending' state and the user
 * sets their own credential by accepting the emailed invite, which is also what
 * verifies the address. Admins never see or choose another user's password.
 * @param {Object} data - User creation data
 * @param {string} data.email - User email (also the login identifier)
 * @param {string} [data.username] - Optional display username, not used to log in
 * @param {string} data.first_name - First name
 * @param {string} [data.middle_name] - Middle name
 * @param {string} data.last_name - Last name
 * @param {string} data.person_type - Person type (staff, guardian)
 * @param {string} [data.phone] - Phone number
 * @param {string} [data.role_id] - Role UUID to assign
 * @param {Array} [data.addresses] - Person addresses
 * @param {boolean} [data.send_invite] - Whether to email the invite (default true)
 * @returns {Promise} Created user data
 */
export const createUser = (data) => post('/users', data);

/**
 * Update user by ID
 * @param {string} userId - User UUID
 * @param {Object} data - User data to update
 * @returns {Promise} Updated user data
 */
export const updateUser = (userId, data) => put(`/users/${userId}`, data);

/**
 * Delete user (soft delete)
 * @param {string} userId - User UUID
 * @returns {Promise} Delete response
 */
export const deleteUser = (userId) => del(`/users/${userId}`);

/**
 * Activate user account
 * @param {string} userId - User UUID
 * @returns {Promise} Activated user data
 */
export const activateUser = (userId) => post(`/users/${userId}/activate`);

/**
 * Send a password-reset link to a user (admin action).
 *
 * Takes no password: admins never set another user's credential. The backend
 * emails a single-use link and invalidates the user's existing sessions.
 * @param {string} userId - User UUID
 * @returns {Promise} { message }
 */
export const resetUserPassword = (userId) => post(`/users/${userId}/reset-password`);

/**
 * Deactivate user account
 * @param {string} userId - User UUID
 * @returns {Promise} Deactivated user data
 */
export const deactivateUser = (userId) => post(`/users/${userId}/deactivate`);

// =============================================================================
// Person/Profile Endpoints
// =============================================================================

/**
 * List persons with optional filtering
 * @param {Object} params - Query parameters
 * @param {string} params.person_type - Filter by type (staff, child, guardian)
 * @param {string} params.status - Filter by status
 * @param {string} params.search - Search term
 * @param {number} params.page - Page number
 * @param {number} params.page_size - Items per page
 * @returns {Promise} Paginated list of persons
 */
export const listPersons = (params = {}) => get('/users/persons', params);

/**
 * Get person by ID
 * @param {string} personId - Person UUID
 * @returns {Promise} Person data with roles
 */
export const getPerson = (personId) => get(`/users/persons/${personId}`);

/**
 * Create a new person (profile record)
 * @param {Object} data - Person data
 */
export const createPerson = (data) => post('/users/persons', data);

/**
 * Update person profile
 * @param {string} personId - Person UUID
 * @param {Object} data - Person data to update
 * @returns {Promise} Updated person data
 */
export const updatePerson = (personId, data) => put(`/users/persons/${personId}`, data);

/**
 * Update a therapist's editable profile fields from the detail header.
 *
 * Narrow, supervisor-safe endpoint: writes only professional-profile fields
 * (name, gender, phone, and the therapist attributes: role_label, designations,
 * experience, joined_date, certifications) on a person who holds the therapist
 * role, within the caller's org. Never touches roles, status, email or perms.
 * @param {string} personId - Therapist's Person UUID
 * @param {Object} data - TherapistProfileUpdate (only send changed fields)
 * @returns {Promise} Updated PersonResponse
 */
export const updateTherapistProfile = (personId, data) => put(`/users/therapists/${personId}/profile`, data);

/**
 * Get staff members
 * @param {Object} params - Query parameters
 * @returns {Promise} List of staff persons
 */
export const getStaff = (params = {}) => get('/users/persons', { ...params, person_type: 'staff' });

/**
 * Get guardians
 * @param {Object} params - Query parameters
 * @returns {Promise} List of guardian persons
 */
export const getGuardians = (params = {}) => get('/users/persons', { ...params, person_type: 'guardian' });

// =============================================================================
// Role Management Endpoints
// =============================================================================

/**
 * List all available roles
 * @returns {Promise} List of roles
 */
export const listRoles = () => get('/users/roles');

/**
 * Get role by ID
 * @param {string} roleId - Role UUID
 * @returns {Promise} Role data with permissions
 */
export const getRole = (roleId) => get(`/users/roles/${roleId}`);

/**
 * Assign role to person
 * @param {string} personId - Person UUID
 * @param {string} roleId - Role UUID
 * @param {boolean} isPrimary - Whether this is the primary role
 * @returns {Promise} Role assignment result
 */
export const assignRole = (personId, roleId, isPrimary = false) =>
  post(`/users/persons/${personId}/roles`, { role_id: roleId, is_primary: isPrimary });

/**
 * Remove role from person
 * @param {string} personId - Person UUID
 * @param {string} roleId - Role UUID
 * @returns {Promise} Role removal result
 */
export const removeRole = (personId, roleId) => del(`/users/persons/${personId}/roles/${roleId}`);

/**
 * Get person's roles
 * @param {string} personId - Person UUID
 * @returns {Promise} List of assigned roles
 */
export const getPersonRoles = (personId) => get(`/users/persons/${personId}/roles`);

// =============================================================================
// Supervisor → Child Allocation (org admin, Edit User dialog)
// =============================================================================

/**
 * Children within a supervisor's hierarchy scope, each flagged whether it is
 * currently allocated to them. Returns { scope_level, scope_label, children }.
 * `scope_level` is null when the person is not an active supervisor.
 * @param {string} personId - Supervisor's Person UUID
 */
export const getSupervisableChildren = (personId) => get(`/users/persons/${personId}/supervisable-children`);

export const updateSupervisorAllocations = (personId, payload) => put(`/users/persons/${personId}/supervisor-allocations`, payload);

// =============================================================================
// Session Management
// =============================================================================

/**
 * Get active sessions for current user
 * @returns {Promise} List of active sessions
 */
export const getActiveSessions = () => get('/users/me/sessions');

/**
 * Invalidate a specific session
 * @param {string} sessionId - Session UUID
 * @returns {Promise} Invalidation result
 */
export const invalidateSession = (sessionId) => del(`/users/me/sessions/${sessionId}`);

/**
 * Invalidate all sessions (logout everywhere)
 * @returns {Promise} Invalidation result
 */
export const invalidateAllSessions = () => del('/users/me/sessions');

// =============================================================================
// Self-service Profile
// =============================================================================

/**
 * Get the signed-in user's own profile, including addresses, the
 * server-derived designation, and email/phone verification state.
 * @returns {Promise} MyProfileResponse
 */
export const getMyProfile = () => get('/users/me/profile');

/**
 * Update the signed-in user's own profile.
 *
 * Only send fields the user is allowed to change. Role, designation, org/center
 * and verification flags are rejected server-side; email and phone move only
 * through the dedicated verification flows below.
 * @param {Object} data - MyProfileUpdate
 * @returns {Promise} Updated MyProfileResponse
 */
export const updateMyProfile = (data) => put('/users/me/profile', data);

/**
 * Upload (replacing any existing) the signed-in user's profile picture.
 * Send a FormData with `display` and `thumbnail` JPEG blobs (produced by
 * utils/avatarImage.js). Returns { profile_image_url }.
 * @param {FormData} formData
 * @param {Function} [onProgress] - upload progress callback (0-100)
 * @returns {Promise} { profile_image_url }
 */
export const uploadMyAvatar = (formData, onProgress) => upload('/profile-pictures/me', formData, onProgress);

/**
 * Remove the signed-in user's profile picture.
 * @returns {Promise} { profile_image_url: null }
 */
export const deleteMyAvatar = () => del('/profile-pictures/me');

// =============================================================================
// Contact Verification
// =============================================================================

/**
 * Get verification state for the signed-in user (used to gate MFA setup).
 * @returns {Promise} ContactVerificationStatus
 */
export const getMyContactStatus = () => get('/users/me/contact');

/**
 * Start an email change. The account's address is NOT changed yet — a
 * confirmation link goes to the new address and the current one stays active
 * until it is confirmed.
 * @param {string} newEmail
 * @param {string} currentPassword - Required re-authentication
 * @returns {Promise} { message, pending_email }
 */
export const requestEmailChange = (newEmail, currentPassword) =>
  post('/users/me/email/change', { new_email: newEmail, current_password: currentPassword });

/**
 * Confirm an email change with the token from the emailed link.
 * Public — the click may land in a browser with no session.
 * @param {string} token
 * @returns {Promise} { message }
 */
export const confirmEmailChange = (token) => post('/users/me/email/confirm', { token });

/**
 * Set/replace the phone number and send a verification OTP.
 * The number is stored immediately but flagged unverified.
 * @param {string} phone - E.164 preferred; the server normalizes
 * @returns {Promise} { message, phone, expires_in }
 */
export const requestPhoneVerification = (phone) => post('/users/me/phone/change', { phone });

/**
 * Confirm the phone number with the OTP sent to it.
 * @param {string} code
 * @returns {Promise} ContactVerificationStatus
 */
export const verifyPhone = (code) => post('/users/me/phone/verify', { code });

/**
 * Change the signed-in user's own password.
 * Re-authenticates with the current password; new-password strength is
 * enforced server-side.
 * @param {string} currentPassword
 * @param {string} newPassword
 * @returns {Promise} { message }
 */
export const changeMyPassword = (currentPassword, newPassword) =>
  post('/users/me/password', { current_password: currentPassword, new_password: newPassword });

// =============================================================================
// Invites
// =============================================================================

/**
 * Validate an invite token and get who it is for (public, unauthenticated).
 * @param {string} token
 * @returns {Promise} InvitePreviewResponse
 */
export const previewInvite = (token) => get('/users/invites/preview', { token });

/**
 * Accept an invite: set the initial password and activate the account.
 * Public. Acceptance also marks the email verified.
 * @param {string} token
 * @param {string} password
 * @returns {Promise} { message, email }
 */
export const acceptInvite = (token, password) => post('/users/invites/accept', { token, password });

/**
 * Re-issue and re-send a user's pending invite (admin action).
 * Mints a fresh token, invalidating the previous link.
 * @param {string} userId
 * @returns {Promise} InviteResponse
 */
export const resendInvite = (userId) => post(`/users/${userId}/invites/resend`);

export default {
  getCurrentUser,
  getMyAccess,
  checkUserAccess,
  lookupUserByEmail,
  grantAccess,
  updateCurrentUser,
  getMyProfile,
  updateMyProfile,
  getMyContactStatus,
  requestEmailChange,
  confirmEmailChange,
  requestPhoneVerification,
  verifyPhone,
  previewInvite,
  acceptInvite,
  resendInvite,
  listUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  activateUser,
  deactivateUser,
  listPersons,
  getPerson,
  updatePerson,
  updateTherapistProfile,
  getStaff,
  getGuardians,
  listRoles,
  getRole,
  assignRole,
  removeRole,
  getPersonRoles,
  getActiveSessions,
  invalidateSession,
  invalidateAllSessions
};
